import fetch from './lib/fetch'

export interface LastOrder {
  id: string
  createdAt: string
  status: string
  displayStatus: string
  lineItems: {
    edges: {
      node: { quantity: number }
    }[]
  }
  totalPriceSet: {
    presentmentMoney: {
      amount: string
    }
  }
  fulfillments: {
    trackingInfo: {
      company: string
      number: string
      url: string
    }[]
  }[]
}

interface LineItem {
  id: string
  name: string
  image: {
    small: string
    medium: string
    large: string
    original: string
    altText: string | null
  }
  quantity: number
  discountedUnitPriceSet: Money
}

interface Money {
  presentmentMoney: {
    amount: string
  }
}

export interface Order {
  id: string
  name: string
  createdAt: string
  customer: { id: string }
  email: string
  discountCode: string | null
  lineItems: { edges: { node: LineItem }[] }
  totalPriceSet: Money
  subtotalPriceSet: Money
  totalShippingPriceSet: Money
  totalTaxSet: Money
  totalDiscountsSet: Money
  totalRefundedSet: Money
  totalReceivedSet: Money
  billingAddress: { formatted: string[] }
  shippingAddress?: { formatted: string[] }
  fulfillments: {
    status: string
    displayStatus: string
    createdAt?: string
    trackingInfo: {
      company: string
      number: string
      url: string
    }[]
  }[]
  transactions: {
    accountNumber: string
    formattedGateway: string
    paymentIcon: {
      url: string
      width: number | null
      height: number | null
    }
  }[]
}

export interface PageInfo {
  hasNextPage: boolean
  hasPreviousPage: boolean
  pageStartCursor: string
  pageEndCursor: string
}

export interface ChargeLineItem {
  purchase_item_id: number
  title: string
  variant_title: string | null
  quantity: number
  total_price: string
  unit_price: string
  grams: number | null
  handle: string | null
  offer_attributes: null
  original_price: string
  properties: never[]
  sku: string
  tax_due: string
  tax_lines: {
    price: string
    rate: string
    title: string
    unit_price: string
  }[]
  taxable: boolean
  taxable_amount: string
  unit_price_includes_tax: boolean
  external_product_id: {
    ecommerce: string
  }
  external_variant_id: {
    ecommerce: string
  }
  images?: {
    small?: string
    medium?: string
    large?: string
    original?: string
  }
  purchase_item_type: 'onetime' | 'subscription'
}

export interface ChargeDiscount {
  id: number
  code: string
  value: number
  value_type: string
}

export interface ChargeShippingLine {
  code: string
  price: string
  source: string
  tax_lines: []
  taxable: boolean
  title: string
  retrieved_at: string
  status: string
}

export interface ChargeAddress {
  address1: string
  address2: string | null
  city: string
  company: string | null
  country_code: string | null
  first_name: string
  last_name: string
  phone: string | null
  province: string | null
  zip: string
}

export interface Charge {
  id: number
  scheduled_at: string
  analytics_data: {
    utm_params: {
      utm_source: string
      utm_medium: string
      utm_campaign: string
    }[]
  }
  client_details: {
    browser_ip: string | null
    user_agent: string | null
  }
  created_at: string
  currency: string
  customer: {
    id: number
    email: string
    external_customer_id: { ecommerce: string }
    hash: string
  }
  external_order_id: { ecommerce: string | null }
  external_transaction_id: { payment_processor: string | null }
  has_uncommitted_changes: boolean
  merged_at: string | null
  note: string | null
  order_attributes: []
  orders_count: number
  original_scheduled_at: string
  payment_processor: string
  processed_at: string | null
  tags: string
  tax_lines: {
    price: string
    rate: string
    title: string
    unit_price: string
  }[]
  taxable: boolean
  taxes_included: boolean
  total_duties: string
  total_refunds: string
  address_id: number
  line_items: ChargeLineItem[]
  discounts: ChargeDiscount[]
  total_price: string
  total_tax: string
  total_line_items_price: string
  total_weight_grams: number
  total_discounts: string
  subtotal_price: string
  discountCode?: string
  shipping_address: ChargeAddress
  billing_address: Address
  type: string
  updated_at: string
  shipping_lines: ChargeShippingLine[]
  status: string
  error: string | null
  error_type: string | null
  retry_date: string | null
}

export async function getNextOrder(rechargeId: string): Promise<Charge> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/next-order`,
  )
}

export interface Address {
  address1: string
  address2: string | null
  city: string
  company: string | null
  country_code: string
  first_name: string
  last_name: string
  phone: string | null
  province: string | null
  zip: string
}

export interface UpcomingOrder {
  id: number | null
  address_id: number
  charge_id: number | null
  currency: string
  line_items: {
    subscription_id: number
    external_product_id: {
      ecommerce: string | null
    }
    external_variant_id: {
      ecommerce: string | null
    }
    images: Record<string, unknown>
    is_skippable: boolean
    is_skipped: boolean
    is_prepaid: boolean
    offer_attributes: Record<string, unknown> | null
    original_price: string
    plan_type: string
    product_title: string
    properties: {
      name: string
      value: string | null
    }[]
    quantity: number
    subtotal_price: string
    unit_price: string
    variant_title: string
  }[]
  order_subtotal: string
  payment_method: {
    id: number
    billing_address: Address
    payment_details: Record<string, unknown>
  }
  shipping_address: Address
}

interface Delivery {
  date: string
  orders: UpcomingOrder[]
}

interface Customer {
  id: number
  email: string
  first_name: string
  last_name: string
}

export interface DeliverySchedule {
  customer: Customer
  deliveries: Delivery[]
}

export async function getOrderSchedule(
  rechargeId: string,
): Promise<DeliverySchedule> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/schedule`,
  )
}

export async function getLastOrder(
  shopifyId: string,
): Promise<LastOrder | null> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/ecommerce/${shopifyId}/last-order`,
  )
}

export async function getOrders(
  shopifyId: string,
  cursor?: string,
  cursorType?: string,
): Promise<{ orders: Order[]; pageInfo: PageInfo }> {
  const qs = new URLSearchParams()
  if (cursor) qs.append('cursor', cursor)
  if (cursorType) qs.append('cursorType', cursorType)
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/ecommerce/${shopifyId}/orders?${qs.toString()}`,
  )
}

export async function getOrder(
  shopifyId: string,
  id: string,
): Promise<{ order: Order }> {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/ecommerce/${shopifyId}/order/${id}`,
  )
}

export async function getCustomerWasteMetrics(shopifyId: string) {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/ecommerce/${shopifyId}/plastic-waste-metrics`,
  )
}

export async function changeDate(rechargeId: string, from: string, to: string) {
  return await fetch(
    `${
      import.meta.env.VITE_API_URL
    }/protected/subscriptions/${rechargeId}/change-date`,
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ from, to }),
    },
  )
}
